/**
 * @file useEula.js
 * @feature EULA
 * @description Manages EULA acceptance state and the POST call to record it.
 *
 * FDD rationale: "accept EULA" is an isolated user-onboarding behaviour.
 * No auth hook should know it exists; no route file should call fetch.
 */

import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import { post_api_headers } from '../../../utils/Store';

const BASE_URL = process.env.REACT_APP_API_USER_PROFILE;

export const useEula = () => {
  const [showEulaModal, setShowEulaModal] = useState(false);
  const [eulaUserId, setEulaUserId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const post_headers = useAtom(post_api_headers)[0];

  /** Called by useUserProfile when eulaAccepted === false */
  const requireEula = useCallback((userId) => {
    setEulaUserId(userId);
    setShowEulaModal(true);
  }, []);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 10) setHasScrolled(true);
  }, []);

  const acceptEula = useCallback(async () => {
    const idToken = sessionStorage.getItem('id_token');
    if (!eulaUserId) return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/eula/accept-eula?userId=${eulaUserId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, ...post_headers },
        },
      );
      if (res.ok) {
        setShowEulaModal(false);
        setIsChecked(false);
        setHasScrolled(false);
      }
    } catch {
      // Silently ignore — user can retry
    }
  }, [eulaUserId, post_headers]);

  return {
    showEulaModal,
    isChecked,
    setIsChecked,
    hasScrolled,
    handleScroll,
    requireEula,
    acceptEula,
  };
};